import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import VersionBadge from '../components/VersionBadge';

const advAxios = () => {
  const token = localStorage.getItem('medulla_token');
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
};

export default function AdvertiserDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [advertiser, setAdvertiser] = useState(null);
  const [tab, setTab] = useState('overview');
  const [campaigns, setCampaigns] = useState([]);
  const [auction, setAuction] = useState(null);
  const [qrPricing, setQrPricing] = useState(null);

  // New campaign form
  const [newCampaign, setNewCampaign] = useState({
    type: 'popup_login', title: '', description: '',
    image_url: '', click_url: '', cta_text: 'Learn More'
  });
  const [bidAmount, setBidAmount] = useState('');
  const [bidCampaign, setBidCampaign] = useState('');
  const [qrCampaign, setQrCampaign] = useState('');
  const [qrWeeks, setQrWeeks] = useState(1);

  useEffect(() => {
    const token = localStorage.getItem('medulla_token');
    if (!token) {
      navigate('/advertiser/auth');
      return;
    }
    loadData();

      // Handle Stripe redirect
    const sid = searchParams.get('session_id');
    if (searchParams.get('bid_success') && sid) {
      advAxios().get(`/medulla/auction/status/${sid}`).then(() => {
        toast.success('Bid submitted & payment authorized!');
      });
    }
    if (searchParams.get('qr_success') && sid) {
      advAxios().get(`/medulla/qr-marketplace/status/${sid}`).then(() => {
        toast.success('QR ad purchase confirmed!');
      });
    }
     
  }, []);

  const loadData = async () => {
    try {
      const [meRes, campsRes, aucRes, qrRes] = await Promise.all([
        advAxios().get('/medulla/advertisers/me'),
        advAxios().get('/medulla/campaigns'),
        advAxios().get('/medulla/auction/current'),
        advAxios().get('/medulla/qr-marketplace/pricing')
      ]);
      setAdvertiser(meRes.data);
      setCampaigns(campsRes.data);
      setAuction(aucRes.data);
      setQrPricing(qrRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('medulla_token');
        navigate('/advertiser/auth');
      }
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    try {
      await advAxios().post('/medulla/campaigns', newCampaign);
      toast.success('Campaign submitted for review!');
      setNewCampaign({ type: 'popup_login', title: '', description: '', image_url: '', click_url: '', cta_text: 'Learn More' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleSubmitBid = async () => {
    if (!bidCampaign || !bidAmount) return toast.error('Pick a campaign & amount');
    try {
      const res = await advAxios().post('/medulla/auction/bid', {
        campaign_id: bidCampaign,
        amount: parseFloat(bidAmount)
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bid failed');
    }
  };

  const handlePurchaseQR = async () => {
    if (!qrCampaign) return toast.error('Pick a campaign');
    try {
      const res = await advAxios().post('/medulla/qr-marketplace/purchase', {
        campaign_id: qrCampaign,
        weeks: parseInt(qrWeeks, 10)
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('medulla_token');
    localStorage.removeItem('medulla_advertiser');
    navigate('/advertiser/auth');
  };

  if (!advertiser) return <div className="min-h-screen bg-black text-white flex items-center justify-center">Loading...</div>;

  const popupCampaigns = campaigns.filter(c => c.type === 'popup_login');
  const qrCampaigns = campaigns.filter(c => c.type === 'qr_passport');

  return (
    <div className="min-h-screen bg-black text-white pb-20">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-green-500/30 p-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-mono font-bold">
            <span className="text-green-400">M</span>edulla <span className="text-xs text-zinc-500 ml-2">Marketing Nexus</span>
          </h1>
          <p className="text-xs text-zinc-400">{advertiser.company_name} · {advertiser.email}</p>
        </div>
        <Button data-testid="adv-logout" onClick={logout} variant="outline" size="sm"
          className="border-zinc-700 text-zinc-300">Log Out</Button>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto border-b border-zinc-800 px-2">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'campaigns', label: 'Campaigns' },
          { id: 'auction', label: 'Popup Auction' },
          { id: 'qr', label: 'QR Marketplace' },
          { id: 'create', label: '+ New Campaign' }
        ].map(t => (
          <button
            key={t.id}
            data-testid={`adv-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-3 text-sm whitespace-nowrap ${tab === t.id ? 'border-b-2 border-green-400 text-green-400' : 'text-zinc-400'}`}
          >{t.label}</button>
        ))}
      </div>

      <div className="p-4 space-y-4">
        {tab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-4 bg-zinc-900 border-zinc-800">
              <p className="text-xs text-zinc-400">Total Spent</p>
              <p className="text-2xl font-mono text-green-400">${(advertiser.total_spent || 0).toFixed(2)}</p>
            </Card>
            <Card className="p-4 bg-zinc-900 border-zinc-800">
              <p className="text-xs text-zinc-400">Active Campaigns</p>
              <p className="text-2xl font-mono text-green-400">{campaigns.filter(c => c.status === 'approved').length}</p>
            </Card>
            <Card className="p-4 bg-zinc-900 border-zinc-800">
              <p className="text-xs text-zinc-400">Pending Review</p>
              <p className="text-2xl font-mono text-yellow-400">{campaigns.filter(c => c.status === 'pending_review').length}</p>
            </Card>
            <Card className="p-4 bg-zinc-900 border-zinc-800 md:col-span-3">
              <p className="text-sm font-bold mb-2">How Medulla Works</p>
              <ul className="text-xs text-zinc-400 space-y-1 list-disc pl-4">
                <li><b className="text-green-400">Popup Auction</b> — Weekly sealed-bid auction. Highest bidder&apos;s ad plays as the 8-second skippable popup when patrons log in.</li>
                <li><b className="text-green-400">QR Marketplace</b> — Fixed price ${qrPricing?.price_per_week || 50}/week. Your ad appears inside every patron&apos;s Bar Passport.</li>
                <li>Create a campaign, upload an image URL + click URL, submit a bid or purchase, and Stripe handles payment.</li>
              </ul>
            </Card>
          </div>
        )}

        {tab === 'campaigns' && (
          <div className="space-y-3">
            {campaigns.length === 0 && <p className="text-zinc-500 text-sm">No campaigns yet. Create one!</p>}
            {campaigns.map(c => (
              <Card key={c.id} data-testid={`campaign-${c.id}`} className="p-4 bg-zinc-900 border-zinc-800">
                <div className="flex items-start gap-3">
                  {c.image_url && <img src={c.image_url} alt="" className="w-20 h-20 object-cover rounded" />}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold">{c.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        c.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                        c.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>{c.status}</span>
                      <span className="text-xs text-zinc-500">{c.type === 'popup_login' ? 'Popup' : 'QR Passport'}</span>
                    </div>
                    <p className="text-sm text-zinc-400 mb-1">{c.description}</p>
                    <div className="text-xs text-zinc-500 flex gap-4">
                      <span>Impressions: {c.impressions || 0}</span>
                      <span>Clicks: {c.clicks || 0}</span>
                      <span>CTR: {c.ctr || 0}%</span>
                    </div>
                    {c.admin_note && <p className="text-xs text-red-400 mt-1">Admin note: {c.admin_note}</p>}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {tab === 'auction' && (
          <Card className="p-6 bg-zinc-900 border-green-500/30">
            <h2 className="text-lg font-bold mb-2">Weekly Popup Ad Auction</h2>
            <p className="text-xs text-zinc-400 mb-4">
              Sealed-bid auction. Highest bidder wins the 8-second skippable popup shown to every patron on login next week.
              Bids are NOT refunded — you&apos;re paying for the guaranteed slot if you win.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
              <div><p className="text-zinc-500">Week Starting</p><p className="font-mono text-green-400">{auction?.week_start}</p></div>
              <div><p className="text-zinc-500">Minimum Bid</p><p className="font-mono text-green-400">${auction?.minimum_bid}</p></div>
              <div><p className="text-zinc-500">Bidders So Far</p><p className="font-mono text-green-400">{auction?.total_bidders}</p></div>
              <div><p className="text-zinc-500">Your Current Bid</p><p className="font-mono text-green-400">
                {auction?.my_bid ? `$${auction.my_bid.amount}` : 'None yet'}
              </p></div>
            </div>

            {!auction?.my_bid && (
              <div className="space-y-2">
                <select
                  data-testid="bid-campaign-select"
                  value={bidCampaign}
                  onChange={(e) => setBidCampaign(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded p-2 text-sm"
                >
                  <option value="">Select Popup Campaign...</option>
                  {popupCampaigns.filter(c => c.status !== 'rejected').map(c => (
                    <option key={c.id} value={c.id}>{c.title} ({c.status})</option>
                  ))}
                </select>
                {popupCampaigns.length === 0 && (
                  <p className="text-xs text-yellow-400">Create a &quot;popup_login&quot; campaign first.</p>
                )}
                <Input
                  data-testid="bid-amount"
                  type="number"
                  min={auction?.minimum_bid || 100}
                  step="10"
                  placeholder={`Bid amount (min $${auction?.minimum_bid || 100})`}
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                />
                <Button data-testid="submit-bid-btn" onClick={handleSubmitBid}
                  className="w-full bg-green-500 hover:bg-green-400 text-black">
                  Submit Sealed Bid & Pay
                </Button>
              </div>
            )}
          </Card>
        )}

        {tab === 'qr' && (
          <Card className="p-6 bg-zinc-900 border-green-500/30">
            <h2 className="text-lg font-bold mb-2">QR Passport Marketplace</h2>
            <p className="text-xs text-zinc-400 mb-4">
              Fixed price: <b className="text-green-400">${qrPricing?.price_per_week || 50}/week</b>.
              Your ad appears inside every patron&apos;s Bar Passport with a scannable QR/click link for the entire week(s) you purchase.
            </p>
            <div className="space-y-2">
              <select
                data-testid="qr-campaign-select"
                value={qrCampaign}
                onChange={(e) => setQrCampaign(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded p-2 text-sm"
              >
                <option value="">Select QR Campaign...</option>
                {qrCampaigns.map(c => (
                  <option key={c.id} value={c.id}>{c.title} ({c.status})</option>
                ))}
              </select>
              {qrCampaigns.length === 0 && (
                <p className="text-xs text-yellow-400">Create a &quot;qr_passport&quot; campaign first.</p>
              )}
              <Input
                data-testid="qr-weeks"
                type="number"
                min="1"
                max="12"
                value={qrWeeks}
                onChange={(e) => setQrWeeks(e.target.value)}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Number of weeks (1-12)"
              />
              <p className="text-sm text-zinc-400">
                Total: <b className="text-green-400">${((qrPricing?.price_per_week || 50) * qrWeeks).toFixed(2)}</b>
              </p>
              <Button data-testid="purchase-qr-btn" onClick={handlePurchaseQR}
                className="w-full bg-green-500 hover:bg-green-400 text-black">
                Purchase & Pay
              </Button>
            </div>
          </Card>
        )}

        {tab === 'create' && (
          <Card className="p-6 bg-zinc-900 border-zinc-800">
            <h2 className="text-lg font-bold mb-4">Create New Campaign</h2>
            <form onSubmit={handleCreateCampaign} className="space-y-3">
              <select
                data-testid="new-campaign-type"
                value={newCampaign.type}
                onChange={(e) => setNewCampaign({ ...newCampaign, type: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded p-2 text-sm"
              >
                <option value="popup_login">Popup Login Ad (weekly sealed-bid auction)</option>
                <option value="qr_passport">QR Passport Ad (fixed price marketplace)</option>
              </select>
              <Input data-testid="new-campaign-title" placeholder="Campaign Title" required
                value={newCampaign.title}
                onChange={(e) => setNewCampaign({ ...newCampaign, title: e.target.value })}
                className="bg-zinc-800 border-zinc-700" />
              <Textarea data-testid="new-campaign-desc" placeholder="Description"
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                className="bg-zinc-800 border-zinc-700" />
              <Input data-testid="new-campaign-image" placeholder="Image URL (jpg/png)" required
                value={newCampaign.image_url}
                onChange={(e) => setNewCampaign({ ...newCampaign, image_url: e.target.value })}
                className="bg-zinc-800 border-zinc-700" />
              <Input data-testid="new-campaign-click" placeholder="Click URL (https://...)" required
                value={newCampaign.click_url}
                onChange={(e) => setNewCampaign({ ...newCampaign, click_url: e.target.value })}
                className="bg-zinc-800 border-zinc-700" />
              <Input data-testid="new-campaign-cta" placeholder="CTA Text"
                value={newCampaign.cta_text}
                onChange={(e) => setNewCampaign({ ...newCampaign, cta_text: e.target.value })}
                className="bg-zinc-800 border-zinc-700" />
              <Button data-testid="submit-new-campaign" type="submit"
                className="w-full bg-green-500 hover:bg-green-400 text-black font-bold">
                Submit Campaign for Review
              </Button>
              <p className="text-xs text-zinc-500">
                Admin will review your campaign. Approved campaigns become eligible to bid or purchase.
              </p>
            </form>
          </Card>
        )}
      </div>

      <VersionBadge />
    </div>
  );
}
